<template>
	<AppCardPage>
		<AppCardRow>
			<AppCardList title="Bruto verloop">
				<canvas
					ref="grossMonthlyPaymentChartCanvas"
					class="monthlyPaymentChart"
				/>
			</AppCardList>
			<AppCardList title="Netto verloop">
				<canvas
					ref="netMonthlyPaymentChartCanvas"
					class="monthlyPaymentChart"
				/>
			</AppCardList>
		</AppCardRow>
		<AppCard no-padding>
			<AppCardTable class="maandlasten-table">
				<template #header>
					<AppCardTableHeader
						v-for="header in maandlastenHeaders"
						:key="header.index"
						:header="header"
					/>
				</template>
				<template #body>
					<tr v-for="(value, jaar) in maandlasten.gegevensPerJaar" :key="jaar">
						<td>{{ maandlasten.gegevensPerJaar[jaar]?.jaar }}</td>
						<td>{{ jaar }}</td>
						<AppMoneyCell :value="value.brutoMaandlast" />
						<AppMoneyCell :value="value.renteBedrag" />
						<AppMoneyCell :value="value.aflossing" />
						<AppMoneyCell :value="value.nettoMaandlast" />
						<AppMoneyCell :value="value.voordeel" />
					</tr>
				</template>
			</AppCardTable>
		</AppCard>
	</AppCardPage>
</template>

<script setup lang="ts">
import useMaandlasten from "@/store/financiering/tabs/useMaandlasten";
import { formatNumericWhole } from "@/helpers/inputHelpers";
import { onBeforeUnmount, ref, watchEffect } from "vue";
import type { ChartConfiguration, ChartItem } from "chart.js/auto";
import Chart from "chart.js/auto";
import type { AppCardTableHeaderModel } from "@/models/AppCardTableHeaderModel";
import { AppCardTableHeaderWidthValues } from "@/enums/AppCardTableHeaderWidthValues";
import type { MonthlyPayment } from "@/models/domain/MonthlyPayment";

const { state } = useMaandlasten();

const maandlasten = ref(state);

const brutoMaandlasten = ref<number[]>([]);
const nettoMaandlasten = ref<number[]>([]);
const aflossingPerMaand = ref<number[]>([]);
const years = ref<string[]>([]);

watchEffect(() => {
	brutoMaandlasten.value = [];
	nettoMaandlasten.value = [];
	aflossingPerMaand.value = [];
	years.value = [];

	for (const year in maandlasten.value?.gegevensPerJaar) {
		if (!(year in Object(maandlasten.value?.gegevensPerJaar))) continue;

		const value = maandlasten.value?.gegevensPerJaar[year];
		years.value.push(year);
		if (!value) continue;

		brutoMaandlasten.value.push(value.brutoMaandlast);
		nettoMaandlasten.value.push(value.nettoMaandlast);
		aflossingPerMaand.value.push(value.aflossing);
	}
});

function getOrCreateTooltip(chart: Chart) {
	let tooltipEl = chart.canvas.parentNode?.querySelector("div");

	if (!tooltipEl) {
		tooltipEl = document.createElement("div");
		tooltipEl.classList.add("chartjs-tooltip");

		const tooltip = document.createElement("div");

		tooltipEl.appendChild(tooltip);
		chart.canvas.parentNode?.appendChild(tooltipEl);
	}

	return tooltipEl;
}

function getChartConfig(
	monthlyPayments: number[],
	aflossingPerMaand: number[],
	label1: string,
) {
	const monthlyPaymentChartData: ChartConfiguration<"line"> = {
		type: "line",
		plugins: [
			{
				id: "drawVerticalLine",
				beforeDraw: (chart) => {
					const activeElements = chart.tooltip?.getActiveElements();
					if (activeElements?.length) {
						const x = activeElements[0]?.element.x;
						const yAxis = chart.scales.y;
						if (!x || !yAxis) return;
						const ctx = chart.ctx;
						ctx.save();
						ctx.beginPath();
						ctx.moveTo(x, yAxis.top);
						ctx.lineTo(x, yAxis.bottom);
						ctx.lineWidth = 1;
						ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
						ctx.stroke();
						ctx.restore();
					}
				},
			},
		],
		data: {
			labels: Object.keys(maandlasten.value?.gegevensPerJaar),
			datasets: [
				{
					label: label1,
					data: monthlyPayments,
					backgroundColor: "transparent",
					borderColor: "#9250e9",
					borderWidth: 2,
					fill: "+1",
					pointBackgroundColor: "#6576A1",
				},
				{
					label: "Aflossing",
					data: aflossingPerMaand,
					backgroundColor: (context) => {
						const { ctx } = context.chart;

						const gradient = ctx.createLinearGradient(0, 0, 0, 200);

						gradient.addColorStop(0, "rgba(38,42,53,0.2)");
						gradient.addColorStop(0.7, "rgba(38,42,53,0.0)");

						return gradient;
					},
					borderColor: "#262A35",
					borderWidth: 2,
					fill: "origin",
					pointBackgroundColor: "#262A35",
				},
			],
		},
		options: {
			scales: {
				y: {
					beginAtZero: true,
					grid: {
						lineWidth: 1,
					},
					border: {
						dash: [10, 10],
					},
					ticks: {
						font: {
							size: 14,
						},
						callback: (value) => {
							return `€ ${value}`;
						},
					},
				},
				x: {
					grid: {
						drawOnChartArea: false,
					},
					ticks: {
						font: {
							size: 14,
						},
					},
				},
			},
			elements: {
				point: {
					radius: 0,
					hoverRadius: 3,
				},
			},
			interaction: {
				intersect: false,
				axis: "x",
			},
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					enabled: false,
					position: "average",
					external: (context) => {
						const { chart, tooltip } = context;
						const tooltipEl = getOrCreateTooltip(chart);

						// Hide if no tooltip
						if (tooltip.opacity === 0) {
							tooltipEl.style.opacity = "0";
							return;
						}

						const tooltipRoot = tooltipEl.querySelector("div");
						// Remove old children
						while (tooltipRoot?.firstChild) {
							tooltipRoot.firstChild.remove();
						}

						if (tooltip.body) {
							const titleLines = tooltip.title || [];
							const bodyLines = tooltip.body.map((b) => b.lines);

							const tooltipHead = document.createElement("span");
							tooltipHead.classList.add("chartjs-tooltip-title");

							titleLines.forEach((title) => {
								const text = document.createTextNode(title);

								tooltipHead.appendChild(text);
							});

							tooltipRoot?.appendChild(tooltipHead);

							bodyLines.forEach((body, i) => {
								const colors = tooltip.labelColors[i];

								const tooltipColorBox = document.createElement("span");
								tooltipColorBox.classList.add("chartjs-tooltip-color");
								tooltipColorBox.style.setProperty(
									"--chartjs-tooltip-color",
									typeof colors?.borderColor === "string"
										? colors.borderColor
										: JSON.stringify(colors?.borderColor),
								);

								const tooltipRow = document.createElement("div");
								tooltipRow.classList.add("chartjs-tooltip-row");

								const tooltipRowTexts = document.createElement("div");
								tooltipRowTexts.classList.add("chartjs-tooltip-texts");

								body.forEach((node) => {
									const span = document.createElement("span");
									const text = document.createTextNode(node.toString());
									span.appendChild(text);
									tooltipRowTexts.appendChild(span);
								});

								tooltipRow.appendChild(tooltipColorBox);
								tooltipRow.appendChild(tooltipRowTexts);
								tooltipRoot?.appendChild(tooltipRow);
							});
						}

						const {
							offsetLeft,
							offsetTop: positionY,
							offsetWidth,
						} = chart.canvas;

						tooltipEl.style.opacity = "1";

						if (tooltip.xAlign === "right") {
							const offsetRight = window.innerWidth - offsetLeft - offsetWidth;

							tooltipEl.style.right = `${offsetRight + (offsetWidth - tooltip.caretX)}px`;
							tooltipEl.style.left = "unset";
						} else if (tooltip.xAlign === "left") {
							tooltipEl.style.left = `${offsetLeft + tooltip.caretX}px`;
							tooltipEl.style.right = "unset";
						}

						tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
					},
					callbacks: {
						label: (item) => {
							if (
								typeof item.raw === "number" ||
								typeof item.raw === "string"
							) {
								return [
									`${item.dataset.label}:`,
									`€ ${formatNumericWhole(item.raw)}`,
								];
							}
							return `${item.dataset.label}: ${item.formattedValue}`;
						},
					},
				},
			},
			transitions: {
				active: {
					animation: {
						duration: 0,
					},
				},
			},
		},
	};

	return monthlyPaymentChartData;
}

const grossMonthlyPaymentChartCanvas = ref<ChartItem>();
const netMonthlyPaymentChartCanvas = ref<ChartItem>();

let grossMonthlyPaymentChart: Chart<"line"> | null = null;
let netMonthlyPaymentChart: Chart<"line"> | null = null;

watchEffect(() => {
	grossMonthlyPaymentChart?.destroy();
	netMonthlyPaymentChart?.destroy();

	if (grossMonthlyPaymentChartCanvas.value) {
		grossMonthlyPaymentChart = new Chart(
			grossMonthlyPaymentChartCanvas.value,
			getChartConfig(
				brutoMaandlasten.value,
				aflossingPerMaand.value,
				"Bruto maandlast",
			),
		);
	}

	if (netMonthlyPaymentChartCanvas.value) {
		netMonthlyPaymentChart = new Chart(
			netMonthlyPaymentChartCanvas.value,
			getChartConfig(
				nettoMaandlasten.value,
				aflossingPerMaand.value,
				"Netto maandlast",
			),
		);
	}
});

onBeforeUnmount(() => {
	grossMonthlyPaymentChart?.destroy();
	netMonthlyPaymentChart?.destroy();
});

const maandlastenHeaders: AppCardTableHeaderModel<MonthlyPayment>[] = [
	{
		label: "Maandlasten",
		width: AppCardTableHeaderWidthValues.Fill,
		index: 0,
		properties: {
			isTitle: true,
		},
	},
	{ model: "jaar", label: "Jaar", width: 100, index: 1 },
	{ model: "brutoMaandlast", label: "Bruto", width: 100, index: 2 },
	{ model: "renteBedrag", label: "Rente", width: 100, index: 3 },
	{ model: "aflossing", label: "Aflossing", width: 100, index: 4 },
	{ model: "nettoMaandlast", label: "Netto", width: 100, index: 5 },
	{ model: "voordeel", label: "Voordeel", width: 100, index: 6 },
];
</script>

<style lang="scss">
.maandlasten-table-wrap {
	overflow-x: auto;
	display: flex;
	margin-top: 24px;
	max-width: 1400px;

	thead {
		th {
			position: sticky;
			top: 0;
			z-index: 2;
		}
	}
}

.maandlasten-table {
	border-collapse: collapse;
	border-spacing: 0;
	width: 100%;

	td {
		padding: 12px 8px;
		white-space: nowrap;

		&:first-child {
			padding: 12px 20px;
		}

		&:nth-child(2n) {
			background-color: var(--background-primary);
		}

		.money {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			column-gap: 1rem;
		}
	}

	th,
	td {
		font-size: 0.85rem;
	}

	th:first-child {
		width: 150px;
	}

	.value-row {
		margin-bottom: 0;

		label {
			display: none;
		}

		.input-wrapper {
			flex: 1 1;

			input {
				padding-right: 0;
			}

			.currency-symbol {
				left: 0;
			}
		}
	}
}

.chartjs-tooltip {
	position: absolute;
	padding: 0.375rem 0.5rem;
	pointer-events: none;
	transform: translate(0, -50%);

	background: #fff;
	border-radius: var(--border-radius);
	border: 1px solid var(--border);

	transition: all 0.1s ease;

	font-size: 0.85rem;

	> div {
		display: flex;
		flex-direction: column;
	}

	.chartjs-tooltip-title {
		font-weight: 500;
	}

	.chartjs-tooltip-row {
		display: flex;
		align-items: center;
		gap: 0.375rem;
	}

	.chartjs-tooltip-texts {
		display: flex;
		justify-content: space-between;
		flex-grow: 1;
		gap: 0.5rem;
	}

	.chartjs-tooltip-color {
		width: 16px;
		height: 16px;
		background: var(--chartjs-tooltip-color);
		border-radius: var(--border-radius);
		display: inline-block;
	}
}
</style>
